import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Spin, message, Modal } from 'antd';
import { CheckCircleOutlined, MobileOutlined, ReloadOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Order } from '../../types';
import { generateQRCode, formatAmount } from '../../utils';

const { Text } = Typography;

const QRCodeAuth: React.FC = () => {
  const navigate = useNavigate();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5分钟倒计时
  const [resetting, setResetting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string>(''); // 支付状态记录

  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('currentOrder');

    if (storedOrder) {
      setOrder(JSON.parse(storedOrder));
    } else {
      message.error('支付信息丢失，请重新发起支付');
      // Optionally close the window or navigate to a specific error page
      // window.close(); 
    }
  }, []);

  // 生成二维码 - 移到条件判断之前
  useEffect(() => {
    if (!order) return;
    
    const generatePaymentQRCode = async () => {
      setLoading(true);
      try {
        // 模拟生成支付二维码的数据
        const qrData = JSON.stringify({
          orderNo: order.order_no,
          amount: order.amount,
          timestamp: Date.now(),
          bankUrl: 'https://wx.bankcomm.com/payment/auth'
        });
        
        const qrCodeDataUrl = await generateQRCode(qrData);
        setQrCodeUrl(qrCodeDataUrl);
      } catch (error) {
        message.error('二维码生成失败，请重试');
        console.error('QR Code generation failed:', error);
      } finally {
        setLoading(false);
      }
    };

    generatePaymentQRCode();
  }, [order]);

  // 倒计时 - 移到条件判断之前
  useEffect(() => {
    if (!order) return;
    
    if (timeLeft <= 0) {
      message.error('二维码已过期，请重新发起支付');
      window.close(); // Close the window if QR code expires
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, navigate, order]);

  if (!order) {
    return (
      <div className="empty-container">
        <Text>支付信息丢失，请重新发起支付</Text>
        <Button 
          type="primary" 
          onClick={() => window.close()} // Close the window if order is lost
          style={{ marginTop: 16 }}
        >
          关闭页面
        </Button>
      </div>
    );
  }

  // 取消支付（弹框确认，确认后关闭页面）
  const handleCancelPayment = () => {
    Modal.confirm({
      title: '取消支付',
      content: '确认取消本次支付并关闭当前订单？',
      okText: '确认',
      cancelText: '返回',
      onOk: () => {
        message.info('已取消支付');
        // 尝试关闭页面，若无法关闭则回退上一页
        try {
          window.close();
        } catch (e) {
          // ignore
        }
        setTimeout(() => {
          // 兜底：回退上一页
          try {
            navigate(-1);
          } catch (e) {
            // ignore
          }
        }, 100);
      }
    });
  };

  // 重置二维码
  const handleResetQRCode = async () => {
    setResetting(true);
    try {
      // 模拟重置过程
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success('二维码已更新');
      
      // 重新生成二维码
      const qrData = JSON.stringify({
        orderNo: order.order_no,
        amount: order.amount,
        timestamp: Date.now(),
        bankUrl: 'https://wx.bankcomm.com/payment/auth'
      });
      
      const qrCodeDataUrl = await generateQRCode(qrData);
      setQrCodeUrl(qrCodeDataUrl);
      setTimeLeft(300); // 重置倒计时
    } catch (error) {
      message.error('二维码重置失败');
    } finally {
      setResetting(false);
    }
  };

  // 模拟检查支付状态 - 根据记录的状态进行跳转
  const checkPaymentStatus = async () => {
    if (paymentStatus) {
      // 如果有记录的状态，使用记录的状态进行跳转
      handleStatusNavigation();
      return;
    }

    setChecking(true);
    try {
      // 模拟检查支付状态
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 模拟支付成功（80%概率成功）
      const isSuccess = Math.random() > 0.2;
      
      if (isSuccess) {
        message.success('授权验证成功！');
        sessionStorage.setItem('paymentSuccess', 'true'); // Store success status
        navigate('/payment-process/complete'); // Navigate without state
      } else {
        message.warning('未检测到授权完成，请确认是否已在手机上完成操作');
      }
    } catch (error) {
      message.error('检查支付状态失败');
    } finally {
      setChecking(false);
    }
  };

  // 处理状态选择 - 仅更新状态记录
  const handleStatusSelect = (status: string) => {
    setPaymentStatus(status);
    message.info(`状态已记录：${getStatusText(status)}`);
    console.log('Selected payment status:', status); // Debug log
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'success': return '支付成功';
      case 'failure': return '支付失败';
      case 'processing': return '支付中';
      default: return '';
    }
  };

  // 根据记录的状态进行页面跳转
  const handleStatusNavigation = () => {
    if (!paymentStatus) {
      message.warning('请先选择支付状态');
      return;
    }
    console.log('Navigating with payment status:', paymentStatus); // Debug log

    switch (paymentStatus) {
      case 'success':
        sessionStorage.setItem('paymentSuccess', 'true'); // Store success status
        console.log('Navigating to /payment-process/complete'); // Debug log
        navigate('/payment-process/complete'); // Navigate without state
        break;
      case 'failure':
        sessionStorage.setItem('paymentSuccess', 'false'); // Store success status
        console.log('Navigating to /payment-process/failure'); // Debug log
        navigate('/payment-process/failure'); // Navigate without state
        break;
      case 'processing':
        sessionStorage.setItem('paymentSuccess', 'false'); // Assuming processing is not success yet
        console.log('Navigating to /payment-process/processing'); // Debug log
        navigate('/payment-process/processing'); // Navigate without state
        break;
      default:
        break;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', paddingTop: 32 }}>

        {/* 二维码卡片 */}
        <Card 
          title={
            <div style={{ textAlign: 'center' }}>
              <MobileOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              使用微信扫码跳转到交行小程序进行授权
            </div>
          }
          style={{ marginBottom: 24 }}
          headStyle={{ background: '#fafafa', fontWeight: 600 }}
        >
          <div className="qrcode-container">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <div className="loading-text">正在生成二维码...</div>
              </div>
            ) : (
              <>
                <div className="qrcode-image">
                  {qrCodeUrl && (
                    <img 
                      src={qrCodeUrl} 
                      alt="支付二维码"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  )}
                </div>
                
                <div className="qrcode-tips">
                  <Space direction="vertical" size={8} style={{ textAlign: 'center' }}>
                    <Text strong style={{ fontSize: 16 }}>
                      支付金额：¥{formatAmount(order.amount)}
                    </Text>
                    <Text type="secondary">
                      请使用微信扫描上方二维码，并跳转至交通银行小程序完成授权
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      
                    </Text>
                    <div style={{ 
                      marginTop: 16, 
                      padding: '8px 16px', 
                      background: timeLeft <= 60 ? '#fff2f0' : '#e6f7ff',
                      borderRadius: 4,
                      color: timeLeft <= 60 ? '#ff4d4f' : '#1890ff'
                    }}>
                      <Text style={{ color: 'inherit' }}>
                        二维码有效时间：{formatTime(timeLeft)}
                      </Text>
                    </div>
                  </Space>
                </div>

                {/* 重置二维码按钮 - 放在二维码下方 */}
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button
                    size="middle"
                    onClick={handleResetQRCode}
                    loading={resetting}
                    icon={<ReloadOutlined />}
                    style={{ width: 140 }}
                  >
                    {resetting ? '重置中...' : '重置二维码'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* 操作指南 */}
        <Card 
          title="操作指南"
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" size={8}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Text strong style={{ marginRight: 8, color: '#1890ff' }}>1.</Text>
              <Text>打开微信APP</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Text strong style={{ marginRight: 8, color: '#1890ff' }}>2.</Text>
              <Text>点击"扫一扫"功能，扫描上方二维码</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Text strong style={{ marginRight: 8, color: '#1890ff' }}>3.</Text>
              <Text>扫描上方二维码</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Text strong style={{ marginRight: 8, color: '#1890ff' }}>4.</Text>
              <Text>确认支付信息并完成验证</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Text strong style={{ marginRight: 8, color: '#1890ff' }}>5.</Text>
              <Text>返回本页面点击"我已完成授权"</Text>
            </div>
          </Space>
        </Card>



        {/* 操作按钮 */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Button
            size="large"
            onClick={handleCancelPayment}
            icon={<CloseOutlined />}
            style={{ width: 120 }}
          >
            取消支付
          </Button>
          <Button
            type="primary"
            size="large"
            loading={checking}
            onClick={checkPaymentStatus}
            icon={<CheckCircleOutlined />}
            style={{ width: 160 }}
          >
            {checking ? '验证中...' : '我已完成授权'}
          </Button>
        </div>

        {/* 遇到问题提示 */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            遇到问题？请检查网络连接或重新生成二维码
          </Text>
        </div>

        {/* 状态记录悬浮栏 - 半透明样式，仅做状态记录 */}
        <div style={{
          position: 'fixed',
          top: 100,
          right: 24,
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          minWidth: '140px',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
        <Text strong style={{ display: 'block', marginBottom: '8px', color: '#1890ff' }}>
          支付状态记录
        </Text>
        {paymentStatus && (
          <div style={{ 
            marginBottom: '8px', 
            padding: '4px 8px', 
            background: 'rgba(24, 144, 255, 0.1)',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            <Text type="secondary">当前状态：</Text>
            <Text strong style={{ color: '#1890ff' }}>
              {getStatusText(paymentStatus)}
            </Text>
          </div>
        )}
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Button 
            size="small" 
            type={paymentStatus === 'success' ? 'primary' : 'text'}
            onClick={() => handleStatusSelect('success')}
            style={{ width: '100%', textAlign: 'left' }}
          >
            支付成功
          </Button>
          <Button 
            size="small" 
            type={paymentStatus === 'failure' ? 'primary' : 'text'}
            onClick={() => handleStatusSelect('failure')}
            style={{ width: '100%', textAlign: 'left' }}
          >
            支付失败
          </Button>
          <Button 
            size="small" 
            type={paymentStatus === 'processing' ? 'primary' : 'text'}
            onClick={() => handleStatusSelect('processing')}
            style={{ width: '100%', textAlign: 'left' }}
          >
            支付中
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default QRCodeAuth;